// Order sets, apply side (PROD-2712): what an applied set leaves on the A/P canvas — boards 6
// (ApAfter) and 6b (ApNeedsDx) of the v3 design, copy verbatim.
//   · the receipt: "5 orders added from Diabetes follow-up. 3 not ordered are listed under the
//     diagnosis." with Undo (the DS UndoSnackbar);
//   · "Not ordered from <set>:" — optional orders with "+ <name>", orders already on the visit
//     with "<name>: already ordered" and "Order anyway", orders no longer orderable, "Hide list";
//   · "Needs a diagnosis" — the waiting orders, "Use suggested <code> <display>" and "Link to
//     another diagnosis";
//   · after Undo: which edited orders it kept, and "Remove all";
//   · when the set placed a medication: the R1 "No medication checks" notice (PROD-2721 AC1).
//
// NESTED UNDER THE DIAGNOSIS (PROD-2723, board 6). With order sets on, the A/P lists each
// diagnosis's orders under it (`ap-dx-order-groups.tsx`) and an application's "Not ordered from
// <set>:" list renders at the foot of the diagnosis it was applied to (`OrderSetLeftovers`, below).
// This block keeps only what has no diagnosis to sit under: "Needs a diagnosis", a list whose
// diagnosis was removed, the Undo outcome, the link picker and the receipt. GRADUATION CANDIDATE
// for Signal DS; built against tokens.
import { useEffect, useState } from 'react';
import { Modal, UndoSnackbar } from '@scripsteam/scrips-signal-ds';
import { Plus } from 'lucide-react';
import { ApOrderRow } from '../ap-order-row';
import { NoMedicationChecksNotice } from '../no-medication-checks-notice';
import type { DxCard } from '../lib/to-patient-assessment';
import type { OrderPayloadMap } from '../lib/order-payload';
import type { DrugInteractionMedInput } from '../lib/to-drug-interaction-input';
import {
  useMedicationInteractionCheck,
  type MedicationInteractionCheckArgs,
} from '../services/use-medication-interaction-check';
import type { AppliedOrderSet, OrderSetApplyState } from './use-order-set-apply';
import { orderCodeOf, plural } from './order-set-apply';
import { hasLeftovers } from './ap-dx-groups';

// Board 6 (ApAfter), measured: a dashed grey-400 box (8px radius, 8 x 10 padding); chips 28px high,
// grey-400 outline, 6px radius, 13px semibold brand blue; the "already ordered" chip amber on amber
// tint with yellow-700 ink; the caption and "Hide list" 12px, grey-700 and grey-600.
const BLOCK = 'flex items-center flex-wrap gap-2 py-2 px-3 border border-dashed border-border-strong rounded-md';
const CHIP_BUTTON =
  'inline-flex items-center gap-1 h-8 py-0 px-3 border border-solid border-border-strong rounded-control bg-surface-card font-sans text-sm font-semibold text-brand-primary cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const WARN_CHIP =
  'inline-flex items-center h-8 py-0 px-3 border border-solid border-status-warning rounded-control bg-status-warning-bg text-sm text-yellow-700 whitespace-nowrap';
const MUTED_CHIP =
  'inline-flex items-center h-8 py-0 px-3 border border-solid border-border-strong rounded-control bg-surface-card text-sm text-text-secondary whitespace-nowrap';
const QUIET_LINK = 'p-0 border-0 bg-transparent font-sans text-size-eyebrow text-text-muted cursor-pointer';
const STATUS_LINE = 'm-0 text-size-sm text-text-secondary';

export interface OrderSetCanvasBlocksProps {
  state: OrderSetApplyState;
  diagnoses: DxCard[];
  readOnly?: boolean;
  /**
   * Applications whose leftovers already render under their diagnosis (PROD-2723,
   * `groupOrdersByDiagnosis().nestedAppKeys`). Their list is skipped here so it shows once.
   */
  nestedAppKeys?: ReadonlySet<string>;
  /** Applications whose "No medication checks" notice renders in their diagnosis card instead. */
  noticeAppKeys?: ReadonlySet<string>;
}

export function OrderSetCanvasBlocks({ state, diagnoses, readOnly, nestedAppKeys, noticeAppKeys }: OrderSetCanvasBlocksProps) {
  const [linkingKey, setLinkingKey] = useState<string | null>(null);
  const linking = linkingKey ? state.applications.find((a) => a.key === linkingKey) : undefined;
  return (
    <>
      {state.notice && (
        <p role="status" className={STATUS_LINE}>
          {state.notice}
        </p>
      )}
      {state.applications.map((app) => (
        <ApplicationBlock
          key={app.key}
          app={app}
          state={state}
          diagnoses={diagnoses}
          readOnly={readOnly}
          leftoversNested={nestedAppKeys?.has(app.key) ?? false}
          noticeNested={noticeAppKeys?.has(app.key) ?? false}
          onLinkOther={() => setLinkingKey(app.key)}
        />
      ))}
      {linking && (
        <Modal open onClose={() => setLinkingKey(null)} title="Link to another diagnosis" size="md">
          <ul className="list-none m-0 p-0 flex flex-col gap-2" aria-label="Diagnoses on this visit">
            {diagnoses.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className={CHIP_BUTTON}
                  onClick={() => {
                    state.resolveNeedsDx(linking.key, { kind: 'dx', dxId: d.id });
                    setLinkingKey(null);
                  }}
                >
                  {d.code ? `${d.code} ${d.display}` : d.display}
                </button>
              </li>
            ))}
          </ul>
        </Modal>
      )}
      {state.receipt && (
        <UndoSnackbar
          key={state.receipt.key}
          message={state.receipt.message}
          onUndo={() => state.undo(state.receipt!.key)}
          onDismiss={state.dismissReceipt}
        />
      )}
    </>
  );
}

function ApplicationBlock({
  app,
  state,
  diagnoses,
  readOnly,
  leftoversNested,
  noticeNested,
  onLinkOther,
}: {
  app: AppliedOrderSet;
  state: OrderSetApplyState;
  diagnoses: DxCard[];
  readOnly?: boolean;
  leftoversNested: boolean;
  noticeNested: boolean;
  onLinkOther: () => void;
}) {
  if (app.undoReceipt) {
    const { removed, keptLabels } = app.undoReceipt;
    return (
      <div role="status" className={BLOCK} aria-label={`Undo of ${app.setName}`}>
        <span className="text-size-xs text-text-secondary">
          {`Removed ${plural(removed, 'order', 'orders')} from ${app.setName}.`}
          {keptLabels.length > 0 &&
            ` Kept ${plural(keptLabels.length, 'edited order', 'edited orders')}: ${keptLabels.join(', ')}.`}
        </span>
        <span className="flex-1" />
        {keptLabels.length > 0 && !readOnly ? (
          <button type="button" className={CHIP_BUTTON} onClick={() => state.removeAll(app.key)}>
            Remove all
          </button>
        ) : (
          <button type="button" className={QUIET_LINK} onClick={() => state.dismissUndoReceipt(app.key)}>
            Dismiss
          </button>
        )}
      </div>
    );
  }

  const waiting = !app.dxId && app.pending.length > 0;
  // PROD-2723: re-read on every render, so a box whose suggested diagnosis was added meanwhile (by
  // another box's "Use suggested", or by hand) stops saying it is missing.
  const suggestedCode = app.suggested?.code?.trim().toLowerCase();
  const suggestedOnVisit = !!suggestedCode && diagnoses.some((d) => d.code?.trim().toLowerCase() === suggestedCode);
  const dx = app.dxId ? diagnoses.find((d) => d.id === app.dxId) : undefined;

  return (
    <>
      {app.placesMedication && !noticeNested && <NoMedicationChecksNotice />}
      {waiting && (
        <section
          aria-label={`Needs a diagnosis: ${app.setName}`}
          className="flex flex-col gap-2 p-3 border border-dashed border-status-warning rounded-lg bg-status-warning-bg"
        >
          <span className="font-bold text-size-eyebrow uppercase text-brand-depth">Needs a diagnosis</span>
          <p className="m-0 text-sm text-text-primary">
            {app.suggested
              ? `${app.setName} suggests ${app.suggested.code}, ${suggestedOnVisit ? 'which is now on this visit' : 'which is not on this visit'}. `
              : `${app.setName} has no saved diagnosis. `}
            {`Sign will ask you to link or remove ${app.pending.length === 1 ? 'this order' : `these ${app.pending.length} orders`}; nothing else waits.`}
          </p>
          {!readOnly && (
            <div className="flex flex-wrap gap-2">
              {app.suggested && (
                <button type="button" className={CHIP_BUTTON} onClick={() => state.resolveNeedsDx(app.key, { kind: 'suggested' })}>
                  {`Use suggested ${app.suggested.code} ${app.suggested.display}`}
                </button>
              )}
              <button type="button" className={CHIP_BUTTON} disabled={diagnoses.length === 0} onClick={onLinkOther}>
                Link to another diagnosis
              </button>
            </div>
          )}
          <div className="flex flex-col gap-2">
            {app.pending.map((d) => (
              <ApOrderRow
                key={d.planOrder.id}
                type={d.planOrder.type}
                name={d.planOrder.label}
                code={orderCodeOf(d.payload)?.code ?? undefined}
                onRemove={!readOnly ? () => state.removePending(app.key, d.planOrder.id) : undefined}
              />
            ))}
          </div>
        </section>
      )}
      {!leftoversNested && hasLeftovers(app) && (
        <OrderSetLeftovers app={app} state={state} readOnly={readOnly} dxCode={dx?.code} />
      )}
    </>
  );
}

/**
 * "Not ordered from <set>:" (board 6) - an application's optional items with "+ <name>", the
 * items already on the visit with "<name>: already ordered" and "Order anyway", the items no longer
 * orderable, and "Hide list". Under its diagnosis (PROD-2723) the caption names only the set; in
 * the canvas blocks (its diagnosis was removed) it also names the diagnosis code it was applied to.
 * Renders whatever the apply listed: it does not decide what is a duplicate.
 */
export function OrderSetLeftovers({
  app,
  state,
  readOnly,
  dxCode,
}: {
  app: AppliedOrderSet;
  state: OrderSetApplyState;
  readOnly?: boolean;
  /** The diagnosis code, named only where the list is NOT under its diagnosis. */
  dxCode?: string;
}) {
  return (
    <div className={BLOCK} aria-label={`Not ordered from ${app.setName}`}>
      <span className="text-size-eyebrow text-text-secondary">
        {`Not ordered from ${app.setName}${dxCode ? ` (${dxCode})` : ''}:`}
      </span>
      {app.optional.map((o) => (
        <button
          key={o.itemId}
          type="button"
          className={CHIP_BUTTON}
          aria-label={`Add ${o.display}`}
          disabled={readOnly}
          onClick={() => state.addListed(app.key, o.itemId, 'optional')}
        >
          <Plus className="size-4" aria-hidden />
          {o.display}
        </button>
      ))}
      {app.duplicates.map((o) => (
        <span key={o.itemId} className="inline-flex items-center gap-2">
          <span className={WARN_CHIP} title={`Already on this visit: ${o.existingLabel}`}>{`${o.display}: already ordered`}</span>
          <button
            type="button"
            className={CHIP_BUTTON}
            aria-label={`Order anyway: ${o.display}`}
            disabled={readOnly}
            onClick={() => state.addListed(app.key, o.itemId, 'duplicate')}
          >
            Order anyway
          </button>
        </span>
      ))}
      {app.notOrderable.map((o) => (
        <span key={o.itemId} className={MUTED_CHIP} title={o.reason}>
          {`${o.display}: no longer orderable`}
        </span>
      ))}
      {app.unsupported.map((o) => (
        <span key={o.itemId} className={MUTED_CHIP}>
          {`${o.display}: add it from the rail`}
        </span>
      ))}
      <span className="flex-1" />
      <button type="button" className={QUIET_LINK} onClick={() => state.hideList(app.key)}>
        Hide list
      </button>
    </div>
  );
}

// ── AC1: the dose dialog's interaction check, for medications a set placed ──────────────────────

export interface AppliedMedicationChecksProps {
  state: OrderSetApplyState;
  payloads: OrderPayloadMap | undefined;
  diagnoses: DxCard[];
  patientId?: string;
  providerId?: string;
  /** Every medication on the plan (the dialog's `existingMedications`), keyed by order id. */
  planMedications: Array<{ orderId: string; med: DrugInteractionMedInput }>;
}

/**
 * Headless: one check per set-placed medication, with the SAME hook and the SAME input the dose
 * dialog uses (`useMedicationInteractionCheck`): this med + the other plan meds, against the
 * patient and the coded diagnoses. While the org has the engine off, nothing is fetched.
 */
export function AppliedMedicationChecks({ state, payloads, diagnoses, patientId, providerId, planMedications }: AppliedMedicationChecksProps) {
  const dxInput = diagnoses
    .filter((d) => !!d.code)
    .map((d) => ({ identifier: d.id, code: d.code as string, display: d.display }));
  return (
    <>
      {state.appliedMedications.map((orderId) => {
        const payload = payloads?.get(orderId);
        if (payload?.kind !== 'medication') return null;
        return (
          <AppliedMedicationCheck
            key={orderId}
            orderId={orderId}
            medication={{ code: payload.medicationCode.code, name: payload.medicationCode.displayName, system: payload.medicationCode.system }}
            patientId={patientId}
            providerId={providerId}
            diagnoses={dxInput}
            existingMedications={planMedications.filter((m) => m.orderId !== orderId).map((m) => m.med)}
            onResult={state.recordInteractions}
          />
        );
      })}
    </>
  );
}

function AppliedMedicationCheck({
  orderId,
  onResult,
  ...args
}: MedicationInteractionCheckArgs & { orderId: string; onResult: (orderId: string, alerts: unknown[]) => void }) {
  const check = useMedicationInteractionCheck(args);
  const data = check.data as unknown[] | undefined;
  useEffect(() => {
    if (data) onResult(orderId, data);
  }, [data, orderId, onResult]);
  return null;
}
